import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { CreditCard, Plus, IndianRupee, Clock, CheckCircle, XCircle, AlertCircle, Calculator, TrendingDown, TrendingUp, Eye, Calendar, BarChart3, Receipt, Paperclip, Info, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { getEmployeeDetails, fetchExistingLoansEMI, applyLoan, fetchLoanHistory, getEmployeeEarningsOverview } from "@/services/employeeService";
import { submitReimbursement, fetchMyReimbursements, cancelReimbursement } from "@/services/reimbursementService";
import ReimbursementDetailsDialog from "@/components/ReimbursementDetailsDialog";
import LoanApplyDialog from "@/components/LoanApplyDialog";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { usePermission } from "@/contexts/RoleAccessContext";
import { Device } from "@capacitor/device";
import { isAuthExpiryError, getErrorMessage } from "@/utils/errorHandler";
import { Skeleton } from "@/components/ui/skeleton";

// Receipt upload limits — kept in sync with the server
// (Zenith-HRMS-Backend/routes/reimbursement.routes.js).
const MAX_RECEIPTS = 5;
const MAX_RECEIPT_SIZE = 5 * 1024 * 1024; // 5 MB per file
const ALLOWED_RECEIPT_MIME = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
];

// Local YYYY-MM-DD (the date picker's min/max use local dates).
const toLocalDateStr = (d) => {
  const x = new Date(d);
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${x.getFullYear()}-${m}-${day}`;
};

const EmployeeLoans = (employeeId) => {
  const { user } = useAuth();
  const [loanRequests, setLoanRequests] = useState([]);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [earningsData, setEarningsData] = useState(null);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [loanTypes, setLoanTypes] = useState([]);
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  ); // Changed to string to support "all"
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [loanToView, setLoanToView] = useState(null);
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [loanEligibility, setLoanEligibility] = useState({
    maxEligible: 0,
    maxEMI: 0,
    grossSalary: 0,
    existingEMI: 0,
    dtiRatio: 50,
  });
  const [formData, setFormData] = useState({
    loan_type: "",
    amount: "",
    tenure_months: "",
    purpose: "",
    monthly_income: "",
    interest_rate: "",
    existing_loans: "",
  });
  const { hasFeature } = useSubscription();
  const { can } = usePermission();
  const canApplyLoan = hasFeature("loan_management");
  // Embedded in the admin employee-details page, the reimbursements tab acts on
  // that employee's claims and follows the admin's access role; an employee's
  // own view is not role-gated.
  const isAdminView = Boolean(employeeId?.employeeId);
  const canViewReimbursements =
    hasFeature("advanced_payroll") &&
    (!isAdminView || can("reimbursement_management", "view"));
  const canSubmitReimbursement =
    !isAdminView || can("reimbursement_management", "create");
  const canCancelReimbursement =
    !isAdminView || can("reimbursement_management", "edit");
  const [device, setDevice] = useState(null);

  // ----- Reimbursements -----
  const [reimbursements, setReimbursements] = useState([]);
  const [showReimbursementDialog, setShowReimbursementDialog] = useState(false);
  const [reimbursementToView, setReimbursementToView] = useState(null);
  const [reimbursementDetailsOpen, setReimbursementDetailsOpen] = useState(false);
  const [submittingReimbursement, setSubmittingReimbursement] = useState(false);
  const [reimbursementClaimTypes, setReimbursementClaimTypes] = useState([]);
  const [claimWindowDays, setClaimWindowDays] = useState(90);
  const [reimbursementPage, setReimbursementPage] = useState(1);
  const reimbursementPageSize = 5;
  const [reimbursementForm, setReimbursementForm] = useState({
    claim_type: "",
    amount: "",
    expense_date: "",
    description: "",
  });
  const [reimbursementFiles, setReimbursementFiles] = useState([]);

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.allSettled([
        fetchLoanRequests(),
        fetchEmployeeData(),
        fetchEarningsOverview(),
        fetchReimbursements(),
      ]);
      setInitialLoading(false);
    };

    loadInitialData();
  }, [selectedYear]);

  useEffect(() => {
    logDeviceInfo();
  }, []);

  const logDeviceInfo = async () => {
    try {
      const info = await Device.getInfo();
      setDevice(info);
    } catch (error) {
      toast.error("Failed to load data")
    }
  };

  const fetchEmployeeData = async () => {
    try {
      if (user) {
        // Fetch employee salary data
        const empResponse = await getEmployeeDetails(employeeId);
        const employee = empResponse;

        // Fetch existing loan EMIs
        const emiResponse = await fetchExistingLoansEMI(employeeId);
        const existingEMI = emiResponse.data.pending_emi || 0;

        setFormData((prev) => ({
          ...prev,
          monthly_income: employee.gross_salary,
          existing_loans: existingEMI.toString(),
        }));

        // Calculate loan eligibility
        calculateLoanEligibility(employee.gross_salary, existingEMI, 24, 10);
      }
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        console.error("Error fetching employee data:", error);
      }
    }
  };

  const fetchEarningsOverview = async () => {
    try {
      setLoadingEarnings(true);
      let response;
      if (selectedYear === "all") {
        response = await getEmployeeEarningsOverview(true, null, employeeId);
      } else {
        response = await getEmployeeEarningsOverview(false, selectedYear, employeeId);
      }

      setEarningsData(response);
    } catch (error) {
      console.error("Error fetching earnings overview:", error);
      if (!isAuthExpiryError(error)) {
        toast.error("Failed to fetch earnings data");
      }
    } finally {
      setLoadingEarnings(false);
    }
  };

  const calculateLoanEligibility = (
    grossSalary,
    existingEMI,
    tenure,
    interestRate,
    dtiRatio = 50
  ) => {
    const maxEMICapacity = grossSalary * (dtiRatio / 100) - existingEMI;

    const N = Number(tenure);
    const annualRate = Number(interestRate);
    const monthlyRate = annualRate / 100 / 12;

    let maxLoanAmount = 0;

    if (maxEMICapacity > 0) {
      if (monthlyRate === 0) {
        // Advance salary case (0% interest)
        maxLoanAmount = maxEMICapacity * N;
      } else {
        const factor = Math.pow(1 + monthlyRate, N);
        maxLoanAmount =
          maxEMICapacity * ((factor - 1) / (monthlyRate * factor));
      }
    }

    maxLoanAmount = Math.floor(maxLoanAmount / 1000) * 1000;

    setLoanEligibility({
      maxEligible: Math.max(0, maxLoanAmount),
      maxEMI: Math.max(0, maxEMICapacity),
      grossSalary,
      existingEMI,
      dtiRatio,
      tenure: N,
      interestRate: annualRate,
    });
  };

  const fetchLoanRequests = async () => {
    try {
      const response = await fetchLoanHistory(employeeId);
      setLoanRequests(response.data.loanHistory);
      setLoanTypes(response.data.loanTypes);
    } catch (error) {
      console.error("Error fetching loan requests:", error);
      if (!isAuthExpiryError(error)) {
        toast.error("Failed to load loan requests");
      }
    }
  };

  const handleSubmitLoan = async (loanData) => {
    try {
      await applyLoan(loanData, employeeId);
      toast.success("Loan request submitted successfully");
      setShowApplyDialog(false);
      await fetchLoanRequests();
    } catch (error) {
      toast.error("Failed to submit loan request");
    }
  };

  const fetchReimbursements = async () => {
    if (!canViewReimbursements) return;
    try {
      const res = await fetchMyReimbursements(employeeId);
      setReimbursements(res?.data?.reimbursements || []);
      setReimbursementClaimTypes(res?.data?.claimTypes || []);
      setClaimWindowDays(res?.data?.claimWindowDays || 90);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        console.error("Error fetching reimbursements:", error);
      }
    }
  };

  const resetReimbursementForm = () => {
    setReimbursementForm({
      claim_type: "",
      amount: "",
      expense_date: "",
      description: "",
    });
    setReimbursementFiles([]);
  };

  // ----- Reimbursement table pagination (5 per page) -----
  const reimbursementTotalPages = Math.max(
    1,
    Math.ceil(reimbursements.length / reimbursementPageSize)
  );

  const pagedReimbursements = React.useMemo(() => {
    const start = (reimbursementPage - 1) * reimbursementPageSize;
    return reimbursements.slice(start, start + reimbursementPageSize);
  }, [reimbursements, reimbursementPage, reimbursementPageSize]);

  useEffect(() => {
    if (reimbursementPage > reimbursementTotalPages) {
      setReimbursementPage(reimbursementTotalPages);
    }
  }, [reimbursementPage, reimbursementTotalPages]);

  const buildReimbursementPages = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = new Set([1, total, current, current - 1, current + 1]);
    const cleaned = [...pages]
      .filter((p) => p >= 1 && p <= total)
      .sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < cleaned.length; i++) {
      result.push(cleaned[i]);
      if (i < cleaned.length - 1 && cleaned[i + 1] - cleaned[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  const handleSubmitReimbursement = async () => {
    const { claim_type, amount, expense_date, description } = reimbursementForm;
    if (!claim_type || !amount || !expense_date || !description.trim()) {
      toast.error("Please fill in type, description, amount and expense date");
      return;
    }
    // Claim date window — mirror the server's hard block (future / too old).
    const [ey, em, ed] = String(expense_date).split("-").map(Number);
    const expenseTime = new Date(ey, em - 1, ed).getTime();
    const now = new Date();
    const todayTime = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    ).getTime();
    if (expenseTime > todayTime) {
      toast.error("Expense date can't be in the future.");
      return;
    }
    if (expenseTime < todayTime - claimWindowDays * 24 * 60 * 60 * 1000) {
      toast.error(
        `Expenses must be claimed within ${claimWindowDays} days of the expense date.`
      );
      return;
    }
    if (reimbursementFiles.length === 0) {
      toast.error(
        "Receipts are mandatory — you must attach valid proof of the expense to submit this claim."
      );
      return;
    }
    // Client-side receipt guards — mirror the server limits so the user gets
    // instant feedback instead of a wasted upload (server still re-validates).
    if (reimbursementFiles.length > MAX_RECEIPTS) {
      toast.error(`You can attach at most ${MAX_RECEIPTS} receipts`);
      return;
    }
    const badType = reimbursementFiles.find(
      (f) => !f.type || !ALLOWED_RECEIPT_MIME.includes(f.type)
    );
    if (badType) {
      toast.error("Only image (JPG, PNG, WEBP, HEIC) and PDF receipts are allowed");
      return;
    }
    const tooBig = reimbursementFiles.find((f) => f.size > MAX_RECEIPT_SIZE);
    if (tooBig) {
      toast.error(`Each receipt must be ${MAX_RECEIPT_SIZE / (1024 * 1024)}MB or smaller`);
      return;
    }
    try {
      setSubmittingReimbursement(true);
      const fd = new FormData();
      fd.append("claim_type", claim_type);
      fd.append("amount", amount);
      fd.append("expense_date", expense_date);
      if (description) fd.append("description", description);
      reimbursementFiles.forEach((file) => fd.append("receipts", file));

      await submitReimbursement(fd, employeeId);
      toast.success("Reimbursement claim submitted");
      setShowReimbursementDialog(false);
      resetReimbursementForm();
      await fetchReimbursements();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to submit reimbursement claim"));
    } finally {
      setSubmittingReimbursement(false);
    }
  };

  const handleCancelReimbursement = async (id) => {
    try {
      await cancelReimbursement(id, employeeId);
      toast.success("Reimbursement claim cancelled");
      await fetchReimbursements();
    } catch (error) {
      toast.error("Failed to cancel reimbursement claim");
    }
  };

  const reimbursementStatusBadge = (status) => {
    const variants = {
      submitted: "secondary",
      approved: "default",
      paid: "default",
      rejected: "destructive",
      cancelled: "outline",
    };
    return <Badge variant={variants[status] || "outline"} className="capitalize">{status}</Badge>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getDisplayStatus = (status) => {
    if (status === "disbursed") return "completed";
    return status;
  };

  const getStatusBadge = (status) => {
    const displayStatus = getDisplayStatus(status);

    const base =
      "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide";

    switch (displayStatus) {
      case "pending":
        return (
          <span className={`${base} border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200`}>
            Pending
          </span>
        );

      case "approved":
        return (
          <span className={`${base} border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200`}>
            Approved
          </span>
        );

      case "completed":
        return (
          <span className={`${base} border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200`}>
            Completed
          </span>
        );

      case "rejected":
        return (
          <span className={`${base} border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200`}>
            Rejected
          </span>
        );

      case "cancelled":
        return (
          <span className={`${base} border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200`}>
            Cancelled
          </span>
        );

      default:
        return null;
    }
  };

  const activeLoans = loanRequests.filter(
    (loan) => loan.status === "approved" && loan.outstanding_amount > 0
  );
  const totalOutstanding = loanRequests
    .filter((loan) => loan.status === "approved")
    .reduce((sum, loan) => sum + (loan.outstanding_amount || 0), 0);
  const totalMonthlyEMI = activeLoans.reduce(
    (sum, loan) => sum + (loan.monthly_emi || 0),
    0
  );

  // Get the primary active loan for detailed view (highest outstanding)
  const primaryLoan =
    activeLoans.length > 0
      ? activeLoans.reduce(
        (max, loan) =>
          loan.outstanding_amount > max.outstanding_amount ? loan : max,
        activeLoans[0]
      )
      : null;

  const handleLoanTypeSelection = (loanTypeName) => {
    const selected = loanTypes.find((t) => t.loan_type === loanTypeName);

    setFormData((prev) => ({
      ...prev,
      loan_type: loanTypeName,
      interest_rate: selected?.interest_rate || 0,
    }));
  };

  const rejectReasonBody = (reason) => (
    <Info
      className="h-4 w-4 text-destructive cursor-pointer "
      onClick={() => {
        setSelectedReason(reason || "No reason provided");
        setShowReasonDialog(true);
      }}
    />
  );

  if (initialLoading) {
    return <EmployeeLoansSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header — hidden when embedded as a tab in the admin employee-details page */}
      {!employeeId.employeeId && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Employee Finances
          </h1>
          <p className="text-gray-500">
            View your salary overview, payroll insights, and earnings analytics
          </p>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="earnings" className="w-full space-y-4">
        <TabsList className="flex justify-start space-x-8 overflow-x-auto overflow-y-hidden touch-pan-x bg-transparent h-auto p-0 no-scrollbar">
          <TabsTrigger
            value="earnings"
            className="w-fit px-4 text-left sm:w-auto"
          >
            Earnings Analytics
          </TabsTrigger>
          {canApplyLoan && device && device?.platform !== "ios" && (
            <TabsTrigger
              value="loans"
              className="w-fit px-4 text-left sm:w-auto"
            >
              Loans & Payroll Insights
            </TabsTrigger>
          )}
          {canViewReimbursements && (
            <TabsTrigger
              value="reimbursements"
              className="w-fit px-4 text-left sm:w-auto"
            >
              Reimbursements
            </TabsTrigger>
          )}
        </TabsList>

        {/* Loans & Advances Tab */}
        {canApplyLoan && device && device?.platform !== "ios" && (
          <TabsContent value="loans" className="space-y-6 mt-6">
            <div className="flex justify-end items-center">
              <Button
                className="flex items-center space-x-2"
                onClick={() => setShowApplyDialog(true)}
              >
                <Plus className="h-4 w-4" />
                <span>Apply for Loan request</span>
              </Button>
            </div>

            {/* Loan Summary Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Total Outstanding
                      </p>
                      <p className="text-2xl font-bold text-red-600">
                        ₹{totalOutstanding.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400">Across all loans</p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <TrendingDown className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Monthly EMI
                      </p>
                      <p className="text-2xl font-bold text-orange-600 dark:text-orange-500">
                        ₹{totalMonthlyEMI.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {activeLoans.length > 0
                          ? `Combined (${activeLoans.length} ${
                              activeLoans.length === 1 ? "loan" : "loans"
                            })`
                          : "No active loans"}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <IndianRupee className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Active Loan Details */}
            {primaryLoan && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Calculator className="h-5 w-5" />
                    <span>Active Loan Details</span>
                    {activeLoans.length > 1 && (
                      <Badge variant="secondary">
                        {activeLoans.length} Active Loans
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Loan Type
                          </p>
                          <p className="font-semibold">
                            {primaryLoan.loan_type}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Loan Amount
                          </p>
                          <p className="font-semibold">
                            ₹{primaryLoan.amount.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Interest Rate
                          </p>
                          <p className="font-semibold">
                            {primaryLoan.interest_rate}% p.a.
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Monthly EMI
                          </p>
                          <p className="font-semibold text-orange-600">
                            ₹{primaryLoan.monthly_emi.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Outstanding
                          </p>
                          <p className="font-semibold text-red-600">
                            ₹{primaryLoan.outstanding_amount.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Paid EMIs
                          </p>
                          <p className="font-semibold text-emerald-500">
                            {primaryLoan.paid_emis} /{" "}
                            {primaryLoan.paid_emis + primaryLoan.remaining_emis}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Repayment Progress</span>
                          <span>
                            {Math.round(
                              (primaryLoan.paid_emis /
                                (primaryLoan.paid_emis +
                                  primaryLoan.remaining_emis)) *
                                100,
                            )}
                            %
                          </span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-emerald-500 h-2 rounded-full"
                            style={{
                              width: `${
                                (primaryLoan.paid_emis /
                                  (primaryLoan.paid_emis +
                                    primaryLoan.remaining_emis)) *
                                100
                              }%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loan Requests History */}
            <Card>
              <CardHeader>
                <CardTitle>Loan History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-gray-200 dark:border-gray-700">
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Type
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Amount
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Tenure
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          EMI
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Status
                        </TableHead>
                        <TableHead className="text-gray-700 dark:text-gray-300">
                          Applied On
                        </TableHead>
                        <TableHead className=" font-semibold text-center w-[200px]">
                          Action
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loanRequests.map((request) => (
                        <TableRow
                          key={request._id}
                          onClick={() => {
                            setLoanToView(request);
                            setShowLoanDetails(true);
                          }}
                          className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                        >
                          <TableCell className="font-medium text-gray-900 dark:text-gray-200">
                            {request.loan_type}
                          </TableCell>
                          <TableCell className="text-gray-800 dark:text-gray-300">
                            ₹{request.amount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-gray-800 dark:text-gray-300">
                            {request.tenure_months} months
                          </TableCell>
                          <TableCell className="text-gray-800 dark:text-gray-300">
                            ₹{request.monthly_emi.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              {getStatusBadge(request.status)}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-800 dark:text-gray-300">
                            {new Date(request.createdAt).toDateString()}
                          </TableCell>
                          <TableCell
                            className="pr-6 py-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1.5">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex items-center group/btn"
                                onClick={() => {
                                  setLoanToView(request);
                                  setShowLoanDetails(true);
                                }}
                              >
                                <Eye className="w-3.5 h-3.5 mr-2 opacity-60 group-hover/btn:text-[#3194a0] group-hover/btn:opacity-100 transition-all" />
                                <span className="group-hover/btn:text-[#3194a0] transition-colors">
                                  View
                                </span>
                                <ChevronRight className="w-3 h-3 ml-1 opacity-0 -translate-x-1 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 group-hover/btn:text-[#3194a0] transition-all" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {loanRequests.length === 0 && (
                  <div className="text-center py-8">
                    <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-300 mb-2">
                      No loan requests
                    </h3>
                    <p className="text-gray-500">
                      Your loan applications will appear here
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Reimbursements Tab (advanced_payroll only) */}
        {canViewReimbursements && (
        <TabsContent value="reimbursements" className="space-y-6 mt-6">
          {canSubmitReimbursement && (
          <div className="flex justify-end items-center">
            <Button
              className="flex items-center space-x-2"
              onClick={() => setShowReimbursementDialog(true)}
            >
              <Plus className="h-4 w-4" />
              <span>Apply for Reimbursement</span>
            </Button>
          </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Reimbursement Claims</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg thin-scrollbar"
                style={{ scrollbarWidth: "thin" }}
              >
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-gray-200 dark:border-gray-700">
                      <TableHead className="text-gray-700 dark:text-gray-300">
                        Type
                      </TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-300">
                        Amount
                      </TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        Expense Date
                      </TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        Submitted At
                      </TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-300">
                        Status
                      </TableHead>
                      <TableHead className="text-gray-700 dark:text-gray-300">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedReimbursements.map((claim) => (
                      <TableRow
                        key={claim._id}
                        className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                        onClick={() => {
                          setReimbursementToView(claim);
                          setReimbursementDetailsOpen(true);
                        }}
                      >
                        <TableCell className="font-medium text-gray-900 dark:text-gray-200">
                          {claim.claim_type}
                        </TableCell>
                        <TableCell className="text-gray-800 dark:text-gray-300">
                          ₹{Number(claim.amount || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-gray-800 dark:text-gray-300">
                          {claim.expense_date
                            ? new Date(claim.expense_date).toLocaleDateString()
                            : "—"}
                        </TableCell>
                        <TableCell className="text-gray-800 dark:text-gray-300 whitespace-nowrap">
                          {claim.created_at || claim.applied_date
                            ? new Date(
                                claim.created_at || claim.applied_date,
                              ).toLocaleDateString()
                            : "—"}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center space-x-2">
                            {reimbursementStatusBadge(claim.status)}
                            {claim.rejection_reason
                              ? rejectReasonBody(claim.rejection_reason)
                              : ""}
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setReimbursementToView(claim);
                                setReimbursementDetailsOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {canCancelReimbursement && claim.status === "submitted" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Cancel claim"
                                className="text-destructive hover:bg-destructive/20 dark:hover:bg-destructive"
                                onClick={() =>
                                  handleCancelReimbursement(claim._id)
                                }
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {reimbursements.length === 0 && (
                <div className="text-center py-8">
                  <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-300 mb-2">
                    No reimbursement claims
                  </h3>
                  <p className="text-gray-500">
                    Your reimbursement claims will appear here
                  </p>
                </div>
              )}

              {reimbursements.length > 0 && (
                <div className="flex justify-end mt-4">
                  <Pagination className="mx-0 justify-end">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          aria-disabled={reimbursementPage === 1}
                          className={
                            reimbursementPage === 1
                              ? "pointer-events-none opacity-50"
                              : ""
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            setReimbursementPage((p) => Math.max(1, p - 1));
                          }}
                        />
                      </PaginationItem>

                      {buildReimbursementPages(
                        reimbursementPage,
                        reimbursementTotalPages
                      ).map((p, idx) => {
                        if (p === "...") {
                          return (
                            <PaginationItem key={`e-${idx}`}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }
                        return (
                          <PaginationItem key={p}>
                            <PaginationLink
                              href="#"
                              isActive={p === reimbursementPage}
                              onClick={(e) => {
                                e.preventDefault();
                                setReimbursementPage(p);
                              }}
                            >
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}

                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          aria-disabled={
                            reimbursementPage === reimbursementTotalPages
                          }
                          className={
                            reimbursementPage === reimbursementTotalPages
                              ? "pointer-events-none opacity-50"
                              : ""
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            setReimbursementPage((p) =>
                              Math.min(reimbursementTotalPages, p + 1)
                            );
                          }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>

          <ReimbursementDetailsDialog
            open={reimbursementDetailsOpen}
            onOpenChange={setReimbursementDetailsOpen}
            claim={reimbursementToView}
          />

          {/* Apply for Reimbursement Dialog */}
          <Dialog
            open={showReimbursementDialog}
            onOpenChange={(open) => {
              if (!open) resetReimbursementForm();
              setShowReimbursementDialog(open);
            }}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Apply for Reimbursement</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={reimbursementForm.claim_type}
                    onValueChange={(val) =>
                      setReimbursementForm((p) => ({ ...p, claim_type: val }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {reimbursementClaimTypes.length === 0 ? (
                        <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                          No reimbursement types available. Please contact your
                          admin.
                        </div>
                      ) : (
                        // Admin-configured types, plus a built-in "Other" catch-all
                        // at the end. Only appended when at least one type exists, so
                        // the empty state above still triggers. Deduped in case an
                        // admin also named a type "Other".
                        [
                          ...reimbursementClaimTypes,
                          ...(reimbursementClaimTypes.some(
                            (t) => String(t).toLowerCase() === "other"
                          )
                            ? []
                            : ["Other"]),
                        ].map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Amount (₹) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={reimbursementForm.amount}
                    onChange={(e) =>
                      setReimbursementForm((p) => ({
                        ...p,
                        amount: e.target.value,
                      }))
                    }
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Expense Date <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="date"
                    max={toLocalDateStr(new Date())}
                    min={toLocalDateStr(
                      Date.now() - claimWindowDays * 24 * 60 * 60 * 1000
                    )}
                    value={reimbursementForm.expense_date}
                    onChange={(e) =>
                      setReimbursementForm((p) => ({
                        ...p,
                        expense_date: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={reimbursementForm.description}
                    onChange={(e) =>
                      setReimbursementForm((p) => ({
                        ...p,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Explain what this reimbursement is for"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Receipts <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                    onChange={(e) =>
                      setReimbursementFiles(Array.from(e.target.files || []))
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Up to {MAX_RECEIPTS} files · images or PDF ·{" "}
                    {MAX_RECEIPT_SIZE / (1024 * 1024)}MB each
                  </p>
                  {reimbursementFiles.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {reimbursementFiles.length} file
                      {reimbursementFiles.length > 1 ? "s" : ""} selected
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      resetReimbursementForm();
                      setShowReimbursementDialog(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitReimbursement}
                    disabled={submittingReimbursement}
                  >
                    {submittingReimbursement ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        {/* Earnings Overview Tab */}
        <TabsContent value="earnings" className="space-y-6 mt-6">
          {/* Year Selector */}
          <div className="flex justify-end items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Year:
              </label>
              <Select
                value={selectedYear}
                onValueChange={(val) => setSelectedYear(val)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {Array.from(
                    { length: 5 },
                    (_, i) => new Date().getFullYear() - i,
                  ).map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {loadingEarnings ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading earnings data...</p>
            </div>
          ) : !earningsData || !earningsData.has_data ? (
            <Card>
              <CardContent className="py-12 text-center">
                <IndianRupee className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  No Earnings Data
                </h3>
                <p className="text-gray-500">
                  Your payslip records will appear here once generated
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Yearly Earnings Summary Cards */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {selectedYear === "all"
                        ? "Total Earnings"
                        : "YTD Earnings"}
                    </CardTitle>
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ₹{earningsData.ytd_earnings.toLocaleString("en-IN")}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {selectedYear === "all"
                        ? "All time earnings"
                        : `January to ${new Date().toLocaleString("default", {
                            month: "long",
                          })}`}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      FY Earnings
                    </CardTitle>
                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ₹{earningsData.fy_earnings.toLocaleString("en-IN")}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {selectedYear === "all"
                        ? "All financial years"
                        : "Financial Year (Apr-Mar)"}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Current Month
                    </CardTitle>
                    <IndianRupee className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ₹
                      {earningsData.current_month_earnings.toLocaleString(
                        "en-IN",
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date().toLocaleString("default", {
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Previous Month
                    </CardTitle>
                    <Clock className="h-4 w-4 text-gray-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ₹
                      {earningsData.previous_month_earnings.toLocaleString(
                        "en-IN",
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(
                        new Date().setMonth(new Date().getMonth() - 1),
                      ).toLocaleString("default", { month: "long" })}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      6-Month Avg
                    </CardTitle>
                    <BarChart3 className="h-4 w-4 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      ₹
                      {earningsData.average_monthly_salary.toLocaleString(
                        "en-IN",
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {selectedYear === "all"
                        ? "Average across all data"
                        : "Average monthly salary"}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Earnings Breakdown Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>
                    {selectedYear === "all"
                      ? "Monthly Analytics Trend (All Years)"
                      : "Monthly Analytics Trend (Last 12 Months)"}
                  </CardTitle>
                  <p className="text-sm text-gray-500">
                    Gross Salary vs Net Salary breakdown
                  </p>
                </CardHeader>
                <CardContent>
                  {earningsData.monthly_trend &&
                  earningsData.monthly_trend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={earningsData.monthly_trend}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          className="stroke-gray-200 dark:stroke-gray-700"
                        />
                        <XAxis
                          dataKey="month_name"
                          className="text-xs"
                          tick={{ fill: "currentColor" }}
                        />
                        <YAxis
                          className="text-xs"
                          tick={{ fill: "currentColor" }}
                          tickFormatter={(value) =>
                            `₹${(value / 1000).toFixed(0)}K`
                          }
                        />
                        <Tooltip
                          formatter={(value) =>
                            `₹${value.toLocaleString("en-IN")}`
                          }
                          contentStyle={{
                            backgroundColor: "var(--background)",
                            border: "1px solid var(--border)",
                            borderRadius: "6px",
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="gross_salary"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          name="Gross Salary"
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="net_salary"
                          stroke="#10b981"
                          strokeWidth={2}
                          name="Net Salary"
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No earnings trend data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Loan Details Modal */}
      <Dialog open={showLoanDetails} onOpenChange={setShowLoanDetails}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Loan Details
            </DialogTitle>
          </DialogHeader>
          {loanToView && (
            <div className="space-y-5 py-2">
              <div className="flex items-center gap-2">
                {getStatusIcon(loanToView.status)}
                {getStatusBadge(loanToView.status)}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Loan Type</p>
                  <p className="font-semibold">{loanToView.loan_type || "—"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Amount</p>
                  <p className="font-semibold">
                    ₹{Number(loanToView.amount || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Tenure</p>
                  <p className="font-semibold">{loanToView.tenure_months} months</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Interest Rate</p>
                  <p className="font-semibold">
                    {loanToView.interest_rate != null
                      ? `${loanToView.interest_rate}% p.a.`
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Monthly EMI</p>
                  <p className="font-semibold text-orange-600">
                    ₹{Number(loanToView.monthly_emi || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Outstanding</p>
                  <p className="font-semibold text-red-600">
                    {loanToView.outstanding_amount != null
                      ? `₹${Number(loanToView.outstanding_amount).toLocaleString()}`
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Paid EMIs</p>
                  <p className="font-semibold text-emerald-500">
                    {loanToView.paid_emis != null
                      ? `${loanToView.paid_emis} / ${loanToView.tenure_months}`
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Applied On</p>
                  <p className="font-semibold">
                    {loanToView.createdAt
                      ? new Date(loanToView.createdAt).toDateString()
                      : "—"}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500">Purpose</p>
                <p className="text-sm text-gray-800 dark:text-gray-200 mt-1 whitespace-pre-wrap">
                  {loanToView.purpose || "—"}
                </p>
              </div>

              {loanToView.rejection_reason && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30">
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">
                    Rejection Reason
                  </p>
                  <p className="mt-1 text-sm text-red-700/90 dark:text-red-300/90 whitespace-pre-wrap">
                    {loanToView.rejection_reason}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showReasonDialog} onOpenChange={setShowReasonDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rejection Reason</DialogTitle>
          </DialogHeader>

          <div className="p-3 text-gray-800 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
            {selectedReason}
          </div>
        </DialogContent>
      </Dialog>

      <LoanApplyDialog
        open={showApplyDialog}
        onOpenChange={(val) => setShowApplyDialog(val)}
        mode="employee"
        title="Apply for Loan"
        maxWidthClass="max-w-md"
        submitLabel="Submit"
        loanTypes={loanTypes}
        employeeGrossSalary={Number(formData.monthly_income || 0)}
        employeeExistingEmi={Number(formData.existing_loans || 0)}
        onDialogOpen={() => {
          fetchEmployeeData(); // refresh salary + existing EMI when opening
        }}
        employeeId={employeeId || null}
        onSubmit={(loanData) => handleSubmitLoan(loanData)}
      />
    </div>
  );
};

export default EmployeeLoans;

function EmployeeLoansSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-52" />
        <Skeleton className="h-4 w-80" />
      </div>
      <Skeleton className="h-12 w-full max-w-md" />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {[...Array(5)].map((_, index) => (
          <Card key={index}>
            <CardHeader className="space-y-2">
              <Skeleton className="h-4 w-28" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-8 w-24" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-72 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}
